import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { MapPin, Map as MapIcon, FileText, ExternalLink } from "lucide-react";
import { ZoomableMap } from "@/components/ZoomableMap";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const mapFileUrl = (id) => `${API}/maps/${id}/file`;
export const mapThumbUrl = (id) => `${API}/maps/${id}/thumb`;

export const MapThumb = ({ m, onOpen }) => (
  <button
    data-testid={`passage-map-${m.id}`}
    onClick={() => onOpen(m)}
    className="group text-left bg-card rounded-2xl border border-border overflow-hidden hover:border-gold transition-colors"
  >
    <div className="aspect-[4/3] bg-ink/5 flex items-center justify-center overflow-hidden">
      {m.content_type === "application/pdf" ? (
        <FileText className="w-10 h-10 text-gold" />
      ) : (
        <img src={mapThumbUrl(m.id)} alt={m.title} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
      )}
    </div>
    <div className="p-4">
      <p className="font-serif text-lg font-600 text-ink leading-snug">{m.title}</p>
      {m.places?.length > 0 && <p className="text-xs text-ink/55 mt-1">{m.places.join(" · ")}</p>}
    </div>
  </button>
);

export const MapViewer = ({ map, onClose }) => (
  <Dialog open={!!map} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="map-viewer-dialog" className="max-w-[1400px] w-[98vw] max-h-[98vh] overflow-y-auto bg-parchment p-3 sm:p-5 gap-2">
      {map && (
        <>
          <DialogTitle className="font-serif text-xl sm:text-2xl text-ink pr-8">{map.title}</DialogTitle>
          <DialogDescription className="text-ink/60">
            {[map.references, map.places?.join(", ")].filter(Boolean).join(" — ")}
          </DialogDescription>
          {map.content_type === "application/pdf" ? (
            <iframe title={map.title} src={mapFileUrl(map.id)} className="w-full h-[70vh] rounded-xl border border-border" />
          ) : (
            <ZoomableMap key={map.id} src={mapFileUrl(map.id)} alt={map.title} />
          )}
          <a href={mapFileUrl(map.id)} target="_blank" rel="noreferrer" data-testid="map-viewer-open-full"
            className="inline-flex items-center gap-1 text-sm text-olive hover:text-gold">
            Open full size <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <p className="text-[11px] text-ink/45">Map © Stephen M. Miller, Casual English Bible® — used under publishing license.</p>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const PassageMaps = ({ places = [], book = "", reference = "" }) => {
  const [maps, setMaps] = useState([]);
  const [open, setOpen] = useState(null);
  const names = places.map((p) => p.name).filter(Boolean);

  useEffect(() => {
    let active = true;
    api.get("/maps/match", { params: { places: names.join(","), book, reference } })
      .then(({ data }) => active && setMaps(data))
      .catch(() => active && setMaps([]));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names.join(","), book, reference]);

  if (!names.length && !maps.length) return null;

  return (
    <div data-testid="passage-maps-section" className="bg-card rounded-2xl border border-border p-7 mt-5">
      <div className="flex items-center gap-2 mb-4">
        <MapIcon className="w-5 h-5 text-gold" />
        <h3 className="font-serif text-2xl font-600 text-ink">Places & Bible Maps</h3>
      </div>
      {names.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="passage-places-list">
          {places.map((p, i) => (
            <div key={p.name} className="flex gap-3 items-start" data-testid={`passage-place-${i}`}>
              <MapPin className="w-4 h-4 text-olive mt-1 shrink-0" />
              <p className="text-sm text-ink/75 leading-relaxed"><span className="font-700 text-ink">{p.name}</span> — {p.note}</p>
            </div>
          ))}
        </div>
      )}
      {maps.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {maps.map((m) => <MapThumb key={m.id} m={m} onOpen={setOpen} />)}
        </div>
      ) : (
        <p data-testid="passage-maps-empty" className="text-xs text-ink/45 mt-5">No ministry map uploaded for these places yet.</p>
      )}
      <MapViewer map={open} onClose={() => setOpen(null)} />
    </div>
  );
};
